import type { Grid } from '~/calendar/views'

import s from './PhoneBar.module.css'

/**
 * The bar fixed to the bottom of a phone, ported from `_calendar_page.py`'s `paintBar`,
 * `goTo` and the four `IC_*` constants.
 *
 * Four places in reach of a thumb. Every one of them was in the header before, where on a
 * phone it was either off the edge of a scrolling row or a 28px target under the browser's
 * own address bar. It exists at ≤720px only — see the stylesheet.
 */

/** What the page is currently doing. */
export type BarMode = 'calendar' | 'briefing' | 'find'

/** The slots, by the key `goTo` dispatches on. */
type Slot = 'list' | Extract<Grid, 'month' | 'week'>

const IC_LIST = (
  <svg
    className={s.mark}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
)

const IC_GRID = (
  <svg
    className={s.mark}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M3.5 4.5h17v15h-17zM3.5 9.5h17M8.5 9.5v10M13.5 9.5v10" />
  </svg>
)


export interface PhoneBarProps {
  /** The grid the calendar is on, or would return to. `list` puts `Month` in the grid slot. */
  grid: Grid
  mode: BarMode
  onGrid(g: Grid): void
}

export function PhoneBar({
  grid,
  mode,
  onGrid,
}: PhoneBarProps) {
  // One slot carries both grids: it shows the one you are on, and switches to the other
  // when you are already there. The label always says which, so the second tap is never a
  // guess about what the first one did.
  const both: Extract<Grid, 'month' | 'week'> = grid === 'week' ? 'week' : 'month'

  function goTo(key: Slot) {
    if (key === 'list') return onGrid('list')
    // The Python read one `mode` variable for both halves of this and got the same answers.
    // Here the grid outlives a detour into the briefing, so "already there" has to ask the
    // mode as well — otherwise a bar labelled `Week`, tapped on the way out of a briefing,
    // would land on Month and the label would have lied.
    const alreadyThere = mode === 'calendar' && grid === both
    onGrid(alreadyThere ? (both === 'month' ? 'week' : 'month') : both)
  }

  // `weekStart` and the scroll to the top are the caller's: `goTo` moved the week anchor to
  // whatever night was on screen before switching, and only the page holding that state can.
  //
  // The same slots for everybody. This used to choose between a wider bar and a narrower
  // one on `me`, because a signed-in reader once had a briefing slot here. That moved to the
  // masthead — "the bar carrying its own was a second door to one room" — and the column it
  // left behind stayed, so a member's bar had its icons crowded into the left of the bar and
  // a strip of empty glass on the right, while a signed-out reader's was laid out correctly.
  // `.three` is the narrower bar's class from then, now a no-op the stylesheet explains.
  return (
    <nav className={`${s.bar} ${s.three}`} aria-label="Sections">
      <button
        className={s.slot}
        type="button"
        aria-pressed={mode === 'calendar' && grid === 'list'}
        onClick={() => goTo('list')}
      >
        {IC_LIST}
        <span>List</span>
      </button>

      <button
        className={s.slot}
        type="button"
        aria-pressed={mode === 'calendar' && grid !== 'list'}
        onClick={() => goTo(both)}
      >
        {IC_GRID}
        <span>{both === 'week' ? 'Week' : 'Month'}</span>
      </button>


      {/*
        * There is no briefing slot, and there was one. It is on the masthead now, which is
        * on every page — the bar carrying its own was a second door to one room, and the
        * count on it had to be kept in step with the count on the other.
        */}
    </nav>
  )
}
